import { jsx as _jsx } from "react/jsx-runtime";
import config from '@iotv/config';
import { existsFilter } from '@iotv/core-services';
import { DeviceTransactionStatus, } from '@iotv/iotv-v3-types';
import { DataGrid, useGridApiRef, } from '@mui/x-data-grid';
import { useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { StatusChip } from '../../common/StatusChip';
import { ItemLink } from '../../../../../../components/common/ItemLinkList';
const debug = process.env.REACT_APP_DEBUG && false;
const getStatusHeaderFunction = (statusBasis, tooltip) => (params) => {
    return _jsx(StatusChip, { state: "info", label: params.colDef.headerName ?? statusBasis, tooltip: tooltip });
};
const getStatusCellFunction = (statusBasis) => (params) => {
    const steps = Object.values(DeviceTransactionStatus);
    const stepComplete = steps.findIndex((item) => item === params.row.status) >= steps.findIndex((item) => item === statusBasis);
    return _jsx(StatusChip, { state: stepComplete ? 'success' : 'pending', label: params.colDef.headerName ?? '', tooltip: statusBasis });
};
const TextHeaderCommon = {
    flex: 1,
    sortable: false,
    resizable: false,
    disableColumnMenu: true,
};
const StatusHeaderCommon = {
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    resizable: false,
    disableColumnMenu: true,
};
const sortDirection = 'desc';
const sortModel = [{ field: 'timestamp', sort: sortDirection }];
const colDefMap = {
    entityName: {
        field: 'entity',
        headerName: 'Entity',
        ...TextHeaderCommon,
        flex: 4,
        valueGetter: (value, row, column, apiRef) => row.entity?.name ?? 'No entity',
        renderCell: ({ row, value }) => row.entity ? (_jsx(ItemLink, { item: row.entity, typographyProps: { paddingTop: 0.875, margin: 0, fontSize: 'inherit', lineHeight: 'calc(var(--height) - 1px' } })) : (value),
    },
    deviceEui: {
        field: 'initialRequest',
        headerName: 'Device EUI',
        ...TextHeaderCommon,
        flex: 4,
        valueGetter: (value, row, column, apiRef) => row.initialRequest?.device.eui ?? 'No device',
        renderCell: ({ row, value }) => row.initialRequest.device ? (_jsx(ItemLink, { item: { ...row.initialRequest.device, name: row.initialRequest.device.eui },
            typographyProps: { paddingTop: 0.875, margin: 0, fontSize: 'inherit', lineHeight: 'calc(var(--height) - 1px' } })) : (value),
    },
    timestamp: {
        field: 'timestamp',
        headerName: 'Time',
        type: 'dateTime',
        ...TextHeaderCommon,
        flex: 4,
        valueFormatter: (value, row, column, apiRef) => new Date(value).toLocaleString(config.locale, {
            year: 'numeric',
            month: '2-digit',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        }),
        renderCell: (params) => {
            return (_jsx(Tooltip, { title: new Date(params.value).toLocaleString(config.locale, {
                    year: 'numeric',
                    month: '2-digit',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                }), children: params.formattedValue }));
        },
    },
    USER_REQUESTED: {
        field: '_1',
        headerName: 'Requested',
        flex: 4,
        ...StatusHeaderCommon,
        renderHeader: getStatusHeaderFunction(DeviceTransactionStatus.USER_REQUESTED, 'The programme set is requested to be sent'),
        renderCell: getStatusCellFunction(DeviceTransactionStatus.USER_REQUESTED),
    },
    SENT_TO_NAS: {
        field: '_2',
        headerName: 'Submitted',
        flex: 4,
        ...StatusHeaderCommon,
        renderHeader: getStatusHeaderFunction(DeviceTransactionStatus.SENT_TO_NAS, 'The programme set has been sent for downlinking'),
        renderCell: getStatusCellFunction(DeviceTransactionStatus.SENT_TO_NAS),
    },
    ACK: {
        field: '_3',
        headerName: 'Transmitted',
        flex: 4,
        ...StatusHeaderCommon,
        renderHeader: getStatusHeaderFunction(DeviceTransactionStatus.ACK, 'The programme set has been acknowledged for downlinking'),
        renderCell: getStatusCellFunction(DeviceTransactionStatus.ACK),
    },
    DEVICE_ACK: {
        field: '_4',
        headerName: 'Received',
        flex: 4,
        ...StatusHeaderCommon,
        renderHeader: getStatusHeaderFunction(DeviceTransactionStatus.DEVICE_ACK, 'The programme set has been recieved by the device'),
        renderCell: getStatusCellFunction(DeviceTransactionStatus.DEVICE_ACK),
    },
    DEVICE_RESPONSE: {
        field: '_5',
        headerName: 'Reported',
        flex: 4,
        ...StatusHeaderCommon,
        renderHeader: getStatusHeaderFunction(DeviceTransactionStatus.DEVICE_RESPONSE, 'The programme set has been reported back by the device'),
        renderCell: getStatusCellFunction(DeviceTransactionStatus.DEVICE_RESPONSE),
    },
};
export const EntityControlTransactionsView = ({ entityTransactions }) => {
    "use no memo";
    // Error: Cannot access refs during render
    const apiRef = useGridApiRef();
    debug && console.log(`INFO: EntityControlTransactionsView rerendered with`, entityTransactions);
    const columns = Object.values(colDefMap);
    const sortFacker = sortDirection === 'desc' ? -1 : 1;
    const rows = entityTransactions
        .map(({ Entity, FifoDCTs }) => {
        return FifoDCTs?.sort((dct1, dct2) => sortFacker * (dct1.timestamp - dct2.timestamp)).map((dct) => ({ ...dct, entity: Entity }));
    })
        .flat(1)
        .filter(existsFilter);
    /** row will have identical ids after updates, so [ provisionally ] the useEffect will be triggered by hash of timestamps
     * TOMRROW timestamps remains the same!!!
     */
    const getRowId = (row) => {
        const { status, id, transactionStages, broadcastScheduleSk, AWS_MessageId, dsk } = row;
        return `${broadcastScheduleSk}-${AWS_MessageId}-${id}-${status}-${transactionStages.length}-${dsk}`;
    };
    debug && console.log(`INFO: EntityControlTransactionsView has`, { entityTransactions, rowsIds: rows.map(getRowId) });
    useEffect(() => {
        if (0 < rows.length)
            apiRef.current?.autosizeColumns({
                includeHeaders: true,
                includeOutliers: true,
                columns: ['entity', 'initialRequest'],
            });
    }, [rows.length]);
    const dataGridParams = {
        getRowId,
        apiRef,
        columns,
        rows,
        sortModel,
        rowSelection: false,
        pageSizeOptions: [10],
        density: 'compact',
        rowSpanning: true,
        initialState: {
            pagination: {
                paginationModel: {
                    page: 0,
                    pageSize: 10,
                },
            },
        },
    };
    return _jsx(DataGrid, { ...dataGridParams });
};
